import AccountCircle from "@mui/icons-material/AccountCircle";
import DescriptionIcon from "@mui/icons-material/Description";
import LogoutIcon from "@mui/icons-material/Logout";
import MenuIcon from "@mui/icons-material/Menu";
import PeopleIcon from "@mui/icons-material/People";
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Avatar,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import * as React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Outlet, useNavigate, useLocation, Navigate } from "react-router-dom";

import LanguageSwitcher from "../../../components/language-switcher";
import { useUiAuth } from "../../../context/UiAuthContext";

const EXPANDED_WIDTH = 240;
const COLLAPSED_WIDTH = 68;

export function AssinaturasLayout(): React.ReactElement {
  const { t } = useTranslation();
  const { isAuthenticated, user, isAdmin, logout } = useUiAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  if (!isAuthenticated) {
    return <Navigate replace state={{ from: location }} to="/assinaturas/login" />;
  }

  const handleDrawerToggle = (): void => {
    setMobileOpen(!mobileOpen);
  };

  const handleCollapseToggle = (): void => {
    setCollapsed(!collapsed);
  };

  const handleLogout = (): void => {
    logout();
    navigate("/assinaturas/login");
  };

  const handleMenu = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (): void => {
    setAnchorEl(null);
  };

  let currentDrawerWidth = EXPANDED_WIDTH;
  if (!isMobile && collapsed) {
    currentDrawerWidth = COLLAPSED_WIDTH;
  }

  const menuItems = [
    {
      text: t("assinaturas.menu.signatures"),
      icon: <DescriptionIcon />,
      path: "/assinaturas",
      active:
        location.pathname === "/assinaturas" ||
        location.pathname === "/assinaturas/create" ||
        Boolean((/^\/assinaturas\/[^/]+$/.exec(location.pathname)) && !location.pathname.startsWith("/assinaturas/admin")),
      visible: true,
    },
    {
      text: t("assinaturas.menu.users"),
      icon: <PeopleIcon />,
      path: "/assinaturas/admin/users",
      active: location.pathname.startsWith("/assinaturas/admin"),
      visible: isAdmin,
    },
  ];

  const drawerContent = (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "#ffffff",
        color: "#1e293b",
        overflowX: "hidden",
      }}
    >
      {/* Menu Items (Sem o cabeçalho duplicado SmartDocs) */}
      <List sx={{ flexGrow: 1, px: 1, py: 2 }}>
        {menuItems
          .filter((item) => item.visible)
          .map((item) => {
            const buttonContent = (
              <ListItemButton
                onClick={() => {
                  navigate(item.path);
                  if (isMobile) setMobileOpen(false);
                }}
                selected={Boolean(item.active)}
                sx={{
                  borderRadius: 2,
                  color: item.active ? "#ffffff" : "#475569",
                  bgcolor: item.active ? "#2F16C9 !important" : "transparent",
                  justifyContent: collapsed && !isMobile ? "center" : "initial",
                  px: collapsed && !isMobile ? 1.5 : 2,
                  "&:hover": {
                    bgcolor: item.active ? "#2F16C9" : "#f1f5f9",
                    color: item.active ? "#ffffff" : "#0f172a",
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    color: item.active ? "#ffffff" : "#64748b",
                    minWidth: collapsed && !isMobile ? "auto" : 40,
                    justifyContent: "center",
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                {(!collapsed || isMobile) ? <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      fontWeight: item.active ? "bold" : "medium",
                      fontSize: "0.95rem",
                    }}
                  /> : null}
              </ListItemButton>
            );

            return (
              <ListItem disablePadding key={item.text} sx={{ mb: 0.5 }}>
                {collapsed && !isMobile ? (
                  <Tooltip placement="right" title={item.text}>
                    {buttonContent}
                  </Tooltip>
                ) : (
                  buttonContent
                )}
              </ListItem>
            );
          })}
      </List>

      <Divider sx={{ borderColor: "#e2e8f0" }} />

      {/* Bottom User Area */}
      <Box
        sx={{
          p: collapsed && !isMobile ? 1.5 : 2,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          justifyContent: collapsed && !isMobile ? "center" : "flex-start",
        }}
      >
        <Avatar sx={{ bgcolor: "#2F16C9", width: 36, height: 36, fontSize: "0.9rem", fontWeight: "bold" }}>
          {user?.name ? user.name.charAt(0).toUpperCase() : "U"}
        </Avatar>
        {(!collapsed || isMobile) ? <Box sx={{ overflow: "hidden" }}>
            <Typography fontWeight="bold" noWrap sx={{ color: "#1e293b" }} variant="subtitle2">
              {user?.name}
            </Typography>
            <Typography noWrap sx={{ color: "#64748b", display: "block" }} variant="caption">
              {user?.email}
            </Typography>
          </Box> : null}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", bgcolor: "#f8fafc" }}>
      {/* Barra Superior de ponta a ponta */}
      <AppBar
        elevation={4}
        position="sticky"
        sx={{
          bgcolor: "#ffffff",
          color: "#1e293b",
          height: "57px",
          justifyContent: "center",
          zIndex: (tTheme) => tTheme.zIndex.drawer + 1,
          borderBottom: "1px solid #e2e8f0",
        }}
      >
        <Toolbar sx={{ minHeight: "57px !important", px: { xs: 2, sm: 3 } }}>
          {/* Botão de colapsar / menu na barra superior */}
          <IconButton
            color="inherit"
            edge="start"
            onClick={isMobile ? handleDrawerToggle : handleCollapseToggle}
            sx={{ mr: 1.5, color: "#475569" }}
            title={collapsed ? "Expandir menu" : "Recolher menu"}
          >
            <MenuIcon />
          </IconButton>

          <Box
            onClick={() => { navigate("/assinaturas"); }}
            sx={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              flexGrow: 1,
            }}
          >
            <img
              alt="logo"
              src="/assets/logo.svg"
              style={{
                height: "auto",
                maxWidth: "90px",
              }}
            />
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <LanguageSwitcher />

            <Box
              onClick={() => { navigate("/assinaturas"); }}
              sx={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                mr: 1,
              }}
            >
              <img
                alt="logo"
                src="/assets/logo2.svg"
                style={{
                  height: "auto",
                  maxWidth: "90px",
                }}
              />
            </Box>

            <Chip
              color={isAdmin ? "primary" : "default"}
              label={isAdmin ? "ADMIN" : "USER"}
              size="small"
              sx={{ fontWeight: "bold" }}
            />

            <IconButton color="inherit" onClick={handleMenu}>
              <AccountCircle sx={{ color: "#475569", fontSize: 28 }} />
            </IconButton>

            <Menu
              anchorEl={anchorEl}
              anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
              onClose={handleClose}
              open={Boolean(anchorEl)}
              transformOrigin={{ horizontal: "right", vertical: "top" }}
            >
              <MenuItem disabled sx={{ opacity: 1, fontWeight: "bold" }}>
                {user?.name}
              </MenuItem>
              <MenuItem disabled sx={{ opacity: 0.8, fontSize: "0.85rem" }}>
                {user?.email}
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleLogout}>
                <LogoutIcon fontSize="small" sx={{ mr: 1, color: "#ef4444" }} />
                <Typography color="error">{t("assinaturas.menu.logout")}</Typography>
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Áreas do Drawer e Conteúdo principal */}
      <Box sx={{ display: "flex", flexGrow: 1, minHeight: 0 }}>
        <Drawer
          ModalProps={{ keepMounted: true }}
          onClose={handleDrawerToggle}
          open={isMobile ? mobileOpen : true}
          sx={{
            width: currentDrawerWidth,
            flexShrink: 0,
            transition: (tTheme) =>
              tTheme.transitions.create("width", {
                easing: tTheme.transitions.easing.sharp,
                duration: tTheme.transitions.duration.enteringScreen,
              }),
            "& .MuiDrawer-paper": {
              width: currentDrawerWidth,
              boxSizing: "border-box",
              borderRight: "1px solid #e2e8f0",
              boxShadow: "2px 0 8px rgba(0,0,0,0.03)",
              top: isMobile ? 0 : "57px",
              height: isMobile ? "100%" : "calc(100vh - 57px)",
              transition: (tTheme) =>
                tTheme.transitions.create("width", {
                  easing: tTheme.transitions.easing.sharp,
                  duration: tTheme.transitions.duration.enteringScreen,
                }),
            },
          }}
          variant={isMobile ? "temporary" : "permanent"}
        >
          {drawerContent}
        </Drawer>

        <Box component="main" sx={{ flexGrow: 1, p: { xs: 2, sm: 3, md: 4 }, minWidth: 0 }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default AssinaturasLayout;
